/**
 * @packageDocumentation
 * @module HackerAPI.Error
 */
/**
 * The wrapper class for all errors that occur inside `hackerapi.js`. It provides a constant API
 * for all types of errors that can occur with the API.
 * @class
 */
var Error = /** @class */ (function () {
    /**
     * Converts a generic Axios error into a specialized HackerAPI error
     */
    function Error(err) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (err.response) {
            if (err.response.status === 404) {
                this.type = Error.NOT_FOUND;
                this.message = "Resource not found";
            }
            else if (err.response.status === 400) {
                this.type = Error.BAD_REQUEST;
                this.message = err.response.data.error || "";
            }
            else if (err.response.status === 401) {
                this.type = Error.UNAUTHORIZED;
                this.message = err.response.data.error || "Unauthorized access";
            }
            else {
                this.type = Error.UNKNOWN_ERROR;
                this.message = "An unknown error occured";
            }
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
        }
        else if (err.request) {
            this.type = Error.NO_RESPONSE;
            this.message = "No response received from server";
        }
        else if (err.message) {
            this.type = Error.UNKNOWN_ERROR;
            this.message = err.message || "Some error occured";
            // Something happened in setting up the request that triggered an Error
        }
        else {
            this.type = Error.FAILED_SETUP;
            this.message = "Setting up the request failed";
        }
        Error.fireListeners(this);
    }
    Object.defineProperty(Error, "NO_RESPONSE", {
        /**
         * The request was made but no response was received from the server.
         * @static
         * @returns
         * @example
         * .catch((err) => {
         *   if (err.type === Error.NO_RESPONSE)
         *     console.log(":(");
         * })
         */
        get: function () {
            return 1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Error, "FAILED_SETUP", {
        /**
         * Something happened in setting up the request that triggered an error
         * @static
         * @returns
         * @example
         * .catch((err) => {
         *   if (err.type === Error.FAILED_SETUP)
         *     console.log(":(");
         * })
         */
        get: function () {
            return 2;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Error, "NOT_FOUND", {
        /**
         * The server 404'ed
         * @static
         * @returns
         */
        get: function () {
            return 3;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Error, "BAD_REQUEST", {
        /**
         * The server 400'ed
         * @static
         * @returns
         */
        get: function () {
            return 4;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Error, "UNAUTHORIZED", {
        /**
         * Either no token is provided or the token isn't authorized to make that request
         * @static
         * @returns
         */
        get: function () {
            return 5;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Error, "UNKNOWN_ERROR", {
        /**
         * What did you do...
         * @static
         * @returns
         */
        get: function () {
            return 6;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Adds a callback to be called whenever a global error is fired
     * @param listener
     * @example
     * let gl = new HackerAPI.Error.GlobalListener(() => alert("Oh no"), [HackerAPI.Error.UNKNOWN_ERROR]);
     * HackerAPI.Error.addGlobalListener(foo);
     */
    Error.addGlobalListener = function (listener) {
        if (!Error.listeners) {
            Error.listeners = [];
        }
        Error.listeners.push(listener);
    };
    /**
     * Removes all instances of the given callback
     * @param listener
     */
    Error.removeGlobalListener = function (listener) {
        if (Error.listeners) {
            for (var i = Error.listeners.length - 1; i >= 0; i--) {
                if (Error.listeners[i] === listener) {
                    Error.listeners.splice(i, 1);
                }
            }
        }
    };
    /**
     * Fires a new error for global error listeners to handle
     * @param error
     */
    Error.fireListeners = function (error) {
        if (Error.listeners) {
            Error.listeners.forEach(function (gl) { return gl.fire(error); });
        }
    };
    return Error;
}());
export default Error;
//# sourceMappingURL=error.js.map